import { Button, Flex, SimpleGrid, TextInput } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useGetEmployeeCandidateRequests } from "../../api/hooks/employeeCandidateRequest/useGetEmployeeCandidateRequests.ts";
import { useUpdateEmployeeCandidateRequest } from "../../api/hooks/employeeCandidateRequest/useUpdateEmployeeCandidateRequest.ts";
import Layout from "../../components/Layout/Layout.tsx";
import { MultiSelect } from "../../components/MultiSelect.tsx";
import Table from "../../components/Table/Table.tsx";
import { SortDirection } from "../../components/Table/types.ts";
import Title from "../../components/Title/Title.tsx";
import { AppRoute } from "../../constants.ts";
import {
  EmployeeCandidateRequestOrderBy,
  OrderBy,
  RolePermission,
  UpdateEmployeeCandidateRequestInput,
} from "../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { useCurrentUserContext } from "../../providers/CurrentUserProvider.tsx";
import { candidateResultCategoryOptions } from "../../types/employeeCandidateRequest.ts";
import { getApiOrderBy } from "../../utils/api.ts";
import { getPagesCount } from "../../utils/pagination.ts";
import { hasPermission } from "../../utils/user.ts";
import { hiringStatusOptions } from "./constants.ts";
import { useEmployeeCandidateRequestTableData } from "./hooks/useEmployeeCandidateRequestTableData.tsx";
import { FiltersValue } from "./types.ts";
import { getEmployeeCandidateFilter } from "./utils.ts";

const EmployeeCandidatePage = () => {
  const navigate = useNavigate();

  const [candidateName, setCandidateName] = useDebouncedState("", 500);
  const [filter, setFilter] = useState<FiltersValue>({});
  const apiFilter = getEmployeeCandidateFilter({ ...filter, candidateName });

  const [orderBy, setOrderBy] = useState<EmployeeCandidateRequestOrderBy>({
    createdAt: OrderBy.Desc,
  });
  const [page, setPage] = useState<number>(1);

  const [{ data: employeeCandidateData, fetching }, refreshData] =
    useGetEmployeeCandidateRequests({
      filter: apiFilter,
      orderBy,
      page,
    });

  const { user } = useCurrentUserContext();

  const [, updateEmployeeCandidate] = useMutationNotificationWrapper(
    useUpdateEmployeeCandidateRequest(),
    {
      success: {
        message: "Employee Candidate Request has been successfully updated.",
      },
    }
  );

  const handleEditEmployeeCandidate = useCallback(
    (id: number, value: UpdateEmployeeCandidateRequestInput) => {
      updateEmployeeCandidate({ id: id, input: value }).then(refreshData);
    },
    [refreshData, updateEmployeeCandidate]
  );

  const hasEditAccess = hasPermission(
    user,
    RolePermission.CanEditEmployeeCandidateClik
  );

  const hasClikResultViewAccess = hasPermission(
    user,
    RolePermission.CanViewClikResult
  );

  const [columns, rows] = useEmployeeCandidateRequestTableData({
    data: employeeCandidateData?.employeeCandidateRequests?.data,
    hasEditAccess,
    hasClikResultViewAccess,
    onEditEmployeeCandidate: handleEditEmployeeCandidate,
  });

  const pageCount = getPagesCount(
    employeeCandidateData?.employeeCandidateRequests?.total
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleOnSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getApiOrderBy(sort));
    },
    [setOrderBy]
  );

  const handleAddLimitRequest = useCallback(() => {
    navigate(AppRoute.EmployeeCandidateCreate);
  }, [navigate]);

  const handleFilterChange = useCallback(
    (key: keyof FiltersValue, value: unknown) => {
      setFilter((prevFilter: FiltersValue) => ({
        ...prevFilter,
        [key]: value,
      }));
    },
    [setFilter]
  );

  return (
    <Layout>
      <Flex direction="column" gap={{ base: 16, sm: 24 }}>
        <Flex direction="column" gap={16}>
          <Title>Employee Candidate</Title>
          {hasEditAccess && (
            <Button
              variant="filledGolden"
              onClick={handleAddLimitRequest}
              uppercase
            >
              add new request
            </Button>
          )}
          <SimpleGrid
            w="100%"
            cols={5}
            breakpoints={[
              { maxWidth: "sm", cols: 2 },
              { maxWidth: "lg", cols: 3 },
            ]}
            spacing={20}
            verticalSpacing={8}
          >
            <TextInput
              size="m"
              label="Candidate Name"
              placeholder="Candidate Name"
              onChange={(event) => {
                setCandidateName(event.target.value);
              }}
            />
            <MultiSelect
              data={candidateResultCategoryOptions}
              label="CLIK Result Category"
              placeholder="CLIK Result Category"
              size="m"
              clearable
              clearButtonProps={{ "aria-label": "Clear selection" }}
              onChange={(value: string[]) =>
                handleFilterChange("clikResultCategory", value)
              }
            />
            <MultiSelect
              data={hiringStatusOptions}
              label="Hiring Status"
              placeholder="Hiring Status"
              size="m"
              clearable
              clearButtonProps={{ "aria-label": "Clear selection" }}
              onChange={(value: string[]) =>
                handleFilterChange("hiringStatus", value)
              }
            />
          </SimpleGrid>
        </Flex>
        <Table
          columns={columns}
          rows={rows}
          defaultSort={{
            columnAccessor: "createdAt",
            direction: SortDirection.desc,
          }}
          pagination={{
            pageCount: pageCount,
            page: page,
            onPageChange: handlePageChange,
          }}
          loading={fetching}
          onSortChange={handleOnSortChange}
        />
      </Flex>
    </Layout>
  );
};

export default EmployeeCandidatePage;
